import { computed, createSSRApp, nextTick, onBeforeUnmount, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";
import { useRouter, type Router } from "vue-router";
import { driver, type Driver, type DriveStep } from "driver.js";
import { renderToString } from "@vue/server-renderer";
import { useAbility } from "@casl/vue";
import { EActions, ESubjects } from "@/api/models/auth/ability";
import { useAuthStore } from "@/stores/authStore";
import { useSettingsStore } from "@/stores/settingsStore";
import { ORGANIZATION_LEVEL_PATHS } from "@/composables/shared/useMyOrganization";
import { EPracticeStaffSettingsKey } from "@/api/models/practice/practiceStaffSettings";
import TourStatusList from "@/components/shared/TourStatusList.vue";

const TOUR_NAV_DRAWER_EVENT = "tour:nav-drawer";

type TourId = "dashboard-main" | "offices-contracts" | "office-billing";

type TourPermissions = {
  canCreatePatient: boolean;
  canReadNotifications: boolean;
  canReadCompany: boolean;
  canManageConnection: boolean;
  canAddReferralContract: boolean;
  canManageBilling: boolean;
};

interface TourContext {
  t: (key: string) => string;
  getIsMobile: () => boolean;
  router: Router;
  getFirstCompanyId: () => string | undefined;
  getFirstPracticeId: () => string | undefined;
  perms: TourPermissions;
}

type TourFactory = (ctx: TourContext) => DriveStep[] | Promise<DriveStep[]>;

const renderDescription = async (
  component: Parameters<typeof createSSRApp>[0],
  props: Record<string, unknown>
): Promise<string> => {
  const app = createSSRApp(component, props);
  return renderToString(app);
};

const activeDriver = ref<Driver | null>(null);
const currentTourId = ref<TourId | null>(null);
const tourDialogVisible = ref(false);
const tutorialDialogDismissedThisSession = ref(false);

type TourExitIntent = "skip" | "dismiss";

let tourExitIntent: TourExitIntent | null = null;

const endTourWithIntent = (driverInstance: Driver | null, intent: TourExitIntent) => {
  if (!driverInstance) {
    return;
  }
  tourExitIntent = intent;
  driverInstance.destroy();
};

const TOUR_COMPLETION_KEYS: Record<TourId, EPracticeStaffSettingsKey> = {
  "dashboard-main": EPracticeStaffSettingsKey.DASHBOARD_TOUR_COMPLETED,
  "offices-contracts": EPracticeStaffSettingsKey.OFFICES_CONTRACTS_TOUR_COMPLETED,
  "office-billing": EPracticeStaffSettingsKey.OFFICE_BILLING_TOUR_COMPLETED,
};

const AVAILABLE_TOURS: Array<{
  id: TourId;
  titleKey: string;
  descriptionKey: string;
  icon: string;
  completionKey: EPracticeStaffSettingsKey;
}> = [
  {
    id: "dashboard-main",
    titleKey: "generic.tour.tourList.tours.dashboardMain.title",
    descriptionKey: "generic.tour.tourList.tours.dashboardMain.description",
    icon: "mdi-view-dashboard",
    completionKey: EPracticeStaffSettingsKey.DASHBOARD_TOUR_COMPLETED,
  },
  {
    id: "offices-contracts",
    titleKey: "generic.tour.tourList.tours.officesContracts.title",
    descriptionKey: "generic.tour.tourList.tours.officesContracts.description",
    icon: "mdi-file-document-outline",
    completionKey: EPracticeStaffSettingsKey.OFFICES_CONTRACTS_TOUR_COMPLETED,
  },
  {
    id: "office-billing",
    titleKey: "generic.tour.tourList.tours.officeBilling.title",
    descriptionKey: "generic.tour.tourList.tours.officeBilling.description",
    icon: "mdi-currency-usd",
    completionKey: EPracticeStaffSettingsKey.OFFICE_BILLING_TOUR_COMPLETED,
  },
];

const dispatchTourNavDrawer = (open: boolean) => {
  globalThis.dispatchEvent(new CustomEvent(TOUR_NAV_DRAWER_EVENT, { detail: { open } }));
};

const waitForElement = (selector: string, timeout = 5000): Promise<Element | null> => {
  return new Promise((resolve) => {
    const existing = document.querySelector(selector);
    if (existing) {
      resolve(existing);
      return;
    }
    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        resolve(el);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => {
      observer.disconnect();
      resolve(document.querySelector(selector));
    }, timeout);
  });
};

const findElementOrBody = (selector: string): Element =>
  (document.querySelector(selector) as Element) ?? (document.body as Element);

const findConnectionsColumn = (columnIndex: number): Element => {
  const th = document.querySelector(
    `[data-tour='tour-connections-table'] th:nth-child(${columnIndex})`
  );
  return (th as Element) ?? findElementOrBody("[data-tour='tour-connections-table']");
};

const computeTourPermissions = (
  can: (action: EActions | string, subject: ESubjects | string) => boolean
): TourPermissions => ({
  canCreatePatient: can(EActions.CREATE, ESubjects.PATIENT__PATIENT),
  canReadNotifications: can(EActions.READ, ESubjects.MESSAGE__NOTIFICATION),
  canReadCompany: can(EActions.READ, ESubjects.PRACTICE__COMPANY),
  canManageConnection: can(EActions.MANAGE_CONNECTION, ESubjects.PRACTICE__CONNECTION),
  canAddReferralContract: can(EActions.ADD_REFERRAL_CONTRACT, ESubjects.PRACTICE__CONTRACT),
  canManageBilling: can("manage_billing", ESubjects.PRACTICE__COMPANY),
});

const canStartTour = (tourId: TourId, perms: TourPermissions): boolean => {
  if (tourId === "offices-contracts") {
    return perms.canReadCompany && perms.canManageConnection;
  }
  return true;
};

const openCompanyConnections = (
  router: Router,
  getFirstCompanyId: () => string | undefined,
  driverInstance: Driver,
  direction: "next" | "previous" = "next"
) => {
  const move = () =>
    direction === "next" ? driverInstance.moveNext() : driverInstance.movePrevious();
  const companyId = getFirstCompanyId();
  if (!companyId) {
    move();
    return;
  }
  router
    .push({ name: "/companies/[id].[[tab]]", params: { id: companyId, tab: "connections" } })
    .then(() => waitForElement("[data-tour='tour-connections-section']").then(move));
};

const tourFactories: Record<TourId, TourFactory> = {
  "dashboard-main": ({ t, getIsMobile, perms }) => {
    const steps: DriveStep[] = [
      {
        element: () => {
          if (getIsMobile()) {
            return findElementOrBody("[data-testid='nav-drawer-toggle']");
          }
          return findElementOrBody("[data-testid='user-menu-button']");
        },
        popover: {
          title: t("generic.tour.dashboard.account.title"),
          description: getIsMobile()
            ? t("generic.tour.dashboard.accountMobile.description")
            : t("generic.tour.dashboard.account.description"),
          side: "bottom",
          align: "center",
        },
      },
    ];
    if (perms.canCreatePatient) {
      steps.push({
        element: "[data-tour='dashboard-add-patient']",
        popover: {
          title: t("generic.tour.dashboard.addPatient.title"),
          description: t("generic.tour.dashboard.addPatient.description"),
          side: "bottom",
          align: "center",
        },
      });
    }
    steps.push({
      element: () => findElementOrBody("[data-tour='dashboard-overview']"),
      popover: {
        title: t("generic.tour.dashboard.dashboardsOverview.title"),
        description: t("generic.tour.dashboard.dashboardsOverview.description"),
        side: "right",
        align: "center",
      },
    });
    if (perms.canReadNotifications) {
      steps.push({
        element: () => findElementOrBody("[data-tour='dashboard-notifications']"),
        popover: {
          title: t("generic.tour.dashboard.notifications.title"),
          description: t("generic.tour.dashboard.notifications.description"),
          side: "left",
          align: "center",
        },
      });
    }
    return steps;
  },

  "offices-contracts": async ({ t, getIsMobile, router, getFirstCompanyId, perms }) => {
    const steps: DriveStep[] = [
      {
        popover: {
          title: t("generic.tour.officesContracts.welcome.title"),
          description: t("generic.tour.officesContracts.welcome.description"),
          popoverClass: "driver-popover--welcome",
          showButtons: ["next", "close"],
        },
      },
      {
        element: () => {
          if (getIsMobile()) {
            return findElementOrBody("[data-testid='nav-drawer-toggle']");
          }
          return findElementOrBody("[data-tour='tour-practices-nav-item']");
        },
        popover: {
          title: t("generic.tour.officesContracts.organizationNav.title"),
          description: getIsMobile()
            ? t("generic.tour.officesContracts.organizationNavMobile.description")
            : t("generic.tour.officesContracts.organizationNav.description"),
          side: "bottom",
          align: "center",
          onNextClick: (_el, _step, opts) => {
            router.push(ORGANIZATION_LEVEL_PATHS.practices).then(() => {
              waitForElement("[data-tour='tour-offices-page']").then(() => {
                opts.driver.moveNext();
              });
            });
          },
        },
      },
      {
        element: () => findElementOrBody("[data-tour='tour-subnav-practices']"),
        popover: {
          title: t("generic.tour.officesContracts.subNav.title"),
          description: t("generic.tour.officesContracts.subNav.description"),
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "[data-tour='tour-offices-page']",
        popover: {
          title: t("generic.tour.officesContracts.practicesPage.title"),
          description: t("generic.tour.officesContracts.practicesPage.description"),
          side: "bottom",
          align: "start",
          onNextClick: (_el, _step, opts) => {
            router.push(ORGANIZATION_LEVEL_PATHS.offices).then(() => {
              waitForElement("[data-tour='tour-offices-list-page']").then(() => {
                opts.driver.moveNext();
              });
            });
          },
        },
      },
      {
        element: () => findElementOrBody("[data-tour='tour-offices-list-page']"),
        popover: {
          title: t("generic.fields.sections.offices"),
          description: t("generic.tour.officesContracts.officesPage.description"),
          side: "bottom",
          align: "start",
          onNextClick: (_el, _step, opts) => {
            openCompanyConnections(router, getFirstCompanyId, opts.driver);
          },
        },
      },
      {
        element: () => findElementOrBody("[data-tour='tour-company-tab-connections']"),
        popover: {
          title: t("generic.tour.officesContracts.connectionsTab.title"),
          description: t("generic.tour.officesContracts.connectionsTab.description"),
          side: "bottom",
          align: "start",
        },
      },
      {
        element: () => findElementOrBody("[data-tour='tour-connections-section']"),
        popover: {
          title: t("generic.tour.officesContracts.connectionsTable.title"),
          description: t("generic.tour.officesContracts.connectionsTable.description"),
          side: "top",
          align: "center",
        },
      },
      {
        element: () => findConnectionsColumn(1),
        popover: {
          title: t("generic.tour.officesContracts.colPractice.title"),
          description: t("generic.tour.officesContracts.colPractice.description"),
          side: "bottom",
        },
      },
      {
        element: () => findConnectionsColumn(2),
        popover: {
          title: t("generic.tour.officesContracts.colReferral.title"),
          description: t("generic.tour.officesContracts.colReferral.description"),
          side: "bottom",
        },
      },
      {
        element: () => findConnectionsColumn(3),
        popover: {
          title: t("generic.tour.officesContracts.colRecordsSender.title"),
          description: t("generic.tour.officesContracts.colRecordsSender.description"),
          side: "bottom",
        },
      },
      {
        element: () => findConnectionsColumn(4),
        popover: {
          title: t("generic.tour.officesContracts.colRecordsReceiver.title"),
          description: t("generic.tour.officesContracts.colRecordsReceiver.description"),
          side: "bottom",
        },
      },
      {
        element: () => findConnectionsColumn(5),
        popover: {
          title: t("generic.tour.officesContracts.colCotreatment.title"),
          description: t("generic.tour.officesContracts.colCotreatment.description"),
          side: "bottom",
        },
      },
    ];

    if (perms.canAddReferralContract) {
      steps.push({
        element: () => {
          const el = document.querySelector("[data-tour='tour-btn-sign-offering']");
          return (el as Element) ?? findConnectionsColumn(3);
        },
        popover: {
          title: t("generic.tour.officesContracts.signOffering.title"),
          description: t("generic.tour.officesContracts.signOffering.description"),
          side: "top",
        },
      });
    }

    steps.push(
      {
        element: () => findElementOrBody("[data-tour='tour-connections-invite']"),
        popover: {
          title: t("generic.tour.officesContracts.invite.title"),
          description: t("generic.tour.officesContracts.invite.description"),
          side: "bottom",
          align: "end",
        },
      },
      {
        element: () => findElementOrBody("[data-tour='tour-connections-table']"),
        popover: {
          title: t("generic.tour.officesContracts.statuses.title"),
          description: await renderDescription(TourStatusList, {
            intro: t("generic.tour.officesContracts.statuses.intro"),
            items: [
              {
                term: t("generic.fields.statusOptions.signed"),
                description: t("generic.tour.officesContracts.statuses.signed.description"),
              },
              {
                term: t("generic.fields.referralContractStatus.draft"),
                description: t("generic.tour.officesContracts.statuses.draft.description"),
              },
              {
                term: t("generic.fields.statusOptions.inactive"),
                description: t("generic.tour.officesContracts.statuses.inactive.description"),
              },
              {
                term: t("features.connections.unavailable"),
                description: t("generic.tour.officesContracts.statuses.unavailable.description"),
              },
              {
                term: t("features.connections.draftContract"),
                description: t("generic.tour.officesContracts.statuses.offer.description"),
              },
            ],
          }),
          side: "top",
        },
      },
      {
        element: () => findElementOrBody("[data-tour='tour-cotreatment-banner']"),
        popover: {
          title: t("generic.tour.officesContracts.cotreatmentBanner.title"),
          description: t("generic.tour.officesContracts.cotreatmentBanner.description"),
          side: "bottom",
          align: "start",
        },
      },
      {
        popover: {
          title: t("generic.tour.officeBilling.end.title"),
          description: t("generic.tour.officesContracts.end.description"),
          showButtons: ["close"],
        },
      }
    );

    return steps;
  },

  "office-billing": ({ t, getIsMobile, router, getFirstCompanyId }) => {
    const moveToCompanyTab = (
      tab: string,
      selector: string,
      driverInstance: Driver,
      direction: "next" | "previous" = "next"
    ) => {
      const move = () =>
        direction === "next" ? driverInstance.moveNext() : driverInstance.movePrevious();
      const companyId = getFirstCompanyId();
      if (!companyId) {
        move();
        return;
      }
      router.push({ path: `/billing/${tab}`, query: { company: companyId } }).then(() => {
        waitForElement(selector).then(move);
      });
    };

    return [
      {
        popover: {
          title: t("generic.tour.officeBilling.welcome.title"),
          description: t("generic.tour.officeBilling.welcome.description"),
          popoverClass: "driver-popover--welcome",
          showButtons: ["next", "close"],
        },
      },
      {
        element: () => {
          if (getIsMobile()) {
            return findElementOrBody("[data-testid='nav-drawer-toggle']");
          }
          return findElementOrBody("[data-tour='tour-practices-nav-item']");
        },
        popover: {
          title: t("generic.tour.officeBilling.userMenu.title"),
          description: getIsMobile()
            ? t("generic.tour.officeBilling.userMenuMobile.description")
            : t("generic.tour.officeBilling.userMenu.description"),
          side: "bottom",
          align: "center",
          onNextClick: (_el, _step, opts) => {
            router.push(ORGANIZATION_LEVEL_PATHS.practices).then(() => {
              waitForElement("[data-tour='tour-offices-page']").then(() => {
                opts.driver.moveNext();
              });
            });
          },
        },
      },
      {
        element: "[data-tour='tour-offices-page']",
        popover: {
          title: t("generic.tour.officeBilling.officesPage.title"),
          description: t("generic.tour.officeBilling.officesPage.description"),
          side: "bottom",
          align: "start",
        },
      },
      {
        element: () => findElementOrBody("[data-testid='my-practices-table']"),
        popover: {
          title: t("generic.fields.company"),
          description: t("generic.tour.officeBilling.companyRow.description"),
          side: "bottom",
          onNextClick: (_el, _step, opts) => {
            moveToCompanyTab("invoices", "[data-tour='tour-billing-history']", opts.driver);
          },
        },
      },
      {
        element: () =>
          findElementOrBody(
            getIsMobile()
              ? "[data-testid='nav-drawer-toggle']"
              : "[data-tour='tour-billing-tab-invoices']"
          ),
        popover: {
          title: t("generic.tour.officeBilling.billingTab.title"),
          description: t("generic.tour.officeBilling.billingTab.overview"),
          side: "bottom",
          onPrevClick: (_el, _step, opts) => {
            router.push(ORGANIZATION_LEVEL_PATHS.practices).then(() => {
              waitForElement("[data-testid='my-practices-table']").then(() => {
                opts.driver.movePrevious();
              });
            });
          },
        },
      },
      {
        element: () => findElementOrBody("[data-tour='tour-billing-history']"),
        popover: {
          title: t("generic.tour.officeBilling.billingHistory.title"),
          description: t("generic.tour.officeBilling.billingHistory.description"),
          side: "top",
          onNextClick: (_el, _step, opts) => {
            moveToCompanyTab(
              "payment-methods",
              "[data-tour='tour-billing-payment-methods']",
              opts.driver
            );
          },
        },
      },
      {
        element: () => findElementOrBody("[data-tour='tour-billing-payment-methods']"),
        popover: {
          title: t("generic.tour.officeBilling.paymentMethods.title"),
          description: t("generic.tour.officeBilling.paymentMethods.description"),
          side: "top",
          onNextClick: (_el, _step, opts) => {
            moveToCompanyTab("general", "[data-tour='tour-billing-balance']", opts.driver);
          },
          onPrevClick: (_el, _step, opts) => {
            moveToCompanyTab(
              "invoices",
              "[data-tour='tour-billing-history']",
              opts.driver,
              "previous"
            );
          },
        },
      },
      {
        element: () => findElementOrBody("[data-tour='tour-billing-balance']"),
        popover: {
          title: t("generic.tour.officeBilling.balance.title"),
          description: t("generic.tour.officeBilling.balance.description"),
          side: "bottom",
          onNextClick: (_el, _step, opts) => {
            moveToCompanyTab("payouts", "[data-tour='tour-billing-payouts']", opts.driver);
          },
          onPrevClick: (_el, _step, opts) => {
            moveToCompanyTab(
              "payment-methods",
              "[data-tour='tour-billing-payment-methods']",
              opts.driver,
              "previous"
            );
          },
        },
      },
      {
        element: () => findElementOrBody("[data-tour='tour-billing-payouts']"),
        popover: {
          title: t("generic.tour.officeBilling.payouts.title"),
          description: t("generic.tour.officeBilling.payouts.withdrawDescription"),
          side: "top",
          onPrevClick: (_el, _step, opts) => {
            moveToCompanyTab(
              "general",
              "[data-tour='tour-billing-balance']",
              opts.driver,
              "previous"
            );
          },
          onNextClick: (_el, _step, opts) => {
            moveToCompanyTab(
              "transactions",
              "[data-tour='tour-billing-transactions']",
              opts.driver
            );
          },
        },
      },
      {
        element: () => findElementOrBody("[data-tour='tour-billing-transactions']"),
        popover: {
          title: t("generic.fields.sections.transactionHistory"),
          description: t("generic.tour.officeBilling.transactions.overviewWithDetails"),
          side: "top",
          onPrevClick: (_el, _step, opts) => {
            moveToCompanyTab(
              "payouts",
              "[data-tour='tour-billing-payouts']",
              opts.driver,
              "previous"
            );
          },
        },
      },
      {
        popover: {
          title: t("generic.tour.officeBilling.end.title"),
          description: t("generic.tour.officeBilling.end.description"),
          showButtons: ["close"],
        },
      },
    ];
  },
};

export const useProductTour = () => {
  const { t } = useI18n();
  const { can } = useAbility();
  const { mdAndDown } = useDisplay();
  const router = useRouter();
  const authStore = useAuthStore();
  const settingsStore = useSettingsStore();

  const isTourActive = computed(() => activeDriver.value?.isActive() ?? false);

  const markTourCompleted = (tourId: TourId) => {
    const key = TOUR_COMPLETION_KEYS[tourId];
    if (key) {
      (settingsStore as unknown as Record<string, boolean>)[key] = true;
    }
  };

  // const areAllToursCompleted = () =>
  //   (Object.values(TOUR_COMPLETION_KEYS) as EPracticeStaffSettingsKey[]).every((key) =>
  //     Boolean((settingsStore as unknown as Record<string, boolean>)[key])
  //   );

  const ensureDriver = () => {
    if (!activeDriver.value) {
      activeDriver.value = driver({
        showProgress: false,
        overlayColor: "rgba(15, 23, 42, 0.6)",
        allowClose: false,
        smoothScroll: false,
        showButtons: ["next", "previous", "close"],
        nextBtnText: t("generic.action.continue"),
        prevBtnText: t("generic.action.back"),
        doneBtnText: t("generic.action.finish"),
        onCloseClick: (_element, _step, options) => {
          endTourWithIntent(options.driver, "skip");
        },
        onDestroyed: () => {
          const tourId = currentTourId.value;
          const exitIntent = tourExitIntent;
          tourExitIntent = null;

          if (tourId === "dashboard-main" && mdAndDown.value) {
            dispatchTourNavDrawer(false);
          }

          if (tourId && exitIntent !== "dismiss") {
            markTourCompleted(tourId);

            router.push("/").then(() => {
              nextTick(() => {
                tourDialogVisible.value = true;
              });
            });
          }

          currentTourId.value = null;
        },
        onPopoverRender: (popover, { state }) => {
          const closeBtn = popover.closeButton;
          closeBtn.innerHTML = t("generic.tour.actions.cancel");
          popover.footer.append(closeBtn);

          const xBtn = document.createElement("button");
          xBtn.type = "button";
          xBtn.className = "driver-popover-x-btn";
          xBtn.setAttribute("aria-label", t("generic.tooltip.close"));
          xBtn.innerHTML = '<span class="mdi mdi-close"></span>';
          xBtn.addEventListener("click", () => {
            endTourWithIntent(activeDriver.value, "dismiss");
          });
          popover.wrapper.insertBefore(xBtn, popover.wrapper.firstChild);

          const activeElement = state.activeElement;
          const popoverWrapper = popover.wrapper;

          if (!activeElement) {
            return;
          }

          requestAnimationFrame(() => {
            if (
              activeElement !== document.body &&
              activeElement.matches(
                [
                  "[data-tour='tour-invoice-form']",
                  "[data-tour='tour-invoice-summary']",
                  "[data-tour='tour-invoice-dialog']",
                ].join(", ")
              )
            ) {
              activeElement.scrollIntoView({
                block: "center",
                inline: "nearest",
                behavior: "auto",
              });
            }

            const elementRect = activeElement.getBoundingClientRect();
            const popoverRect = popoverWrapper.getBoundingClientRect();

            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            const centerX = viewportWidth / 2 - popoverRect.width / 2;
            const centerY = viewportHeight / 2 - popoverRect.height / 2;

            const hasIntersection = !(
              elementRect.right < centerX ||
              elementRect.left > centerX + popoverRect.width ||
              elementRect.bottom < centerY ||
              elementRect.top > centerY + popoverRect.height
            );

            if (!hasIntersection) {
              popoverWrapper.style.left = `${centerX}px`;
              popoverWrapper.style.top = `${centerY}px`;
              popoverWrapper.style.right = "auto";
              popoverWrapper.style.bottom = "auto";
            }
          });
        },
      });
    }

    return activeDriver.value;
  };

  const startTour = async (tourId: TourId, { force = false } = {}) => {
    const instance = ensureDriver();

    if (!force && instance.isActive()) return;

    const perms = computeTourPermissions(can);
    if (!canStartTour(tourId, perms)) {
      return;
    }

    const factory = tourFactories[tourId];
    currentTourId.value = tourId;
    tourExitIntent = null;

    if (tourId === "dashboard-main" && mdAndDown.value) {
      dispatchTourNavDrawer(false);
    }

    const ctx: TourContext = {
      t,
      getIsMobile: () => mdAndDown.value,
      router,
      getFirstCompanyId: () => authStore.loggedAsStaff?.companies?.[0]?.id,
      getFirstPracticeId: () => authStore.loggedAsStaff?.companies?.[0]?.practices?.[0],
      perms,
    };

    const steps = await Promise.resolve(factory(ctx));
    if (steps.length === 0) {
      currentTourId.value = null;
      return;
    }

    instance.setSteps(steps);
    instance.drive();
  };

  const stopTour = () => {
    if (activeDriver.value) {
      tourExitIntent = "dismiss";
      activeDriver.value.destroy();
      activeDriver.value = null;
    }
  };

  onBeforeUnmount(stopTour);

  return {
    startTour,
    stopTour,
    isTourActive,
  };
};

export const useTourAccess = () => {
  const { can } = useAbility();
  const perms = computed(() => computeTourPermissions(can));
  const canStartTourId = (id: TourId) => canStartTour(id, perms.value);
  const missingPermissionsDetailKey = (id: TourId): string | null => {
    if (canStartTourId(id)) {
      return null;
    }
    if (id === "office-billing") {
      return "generic.tour.tourList.missingPermissionsReasons.officeBilling";
    }
    if (id === "offices-contracts") {
      return "generic.tour.tourList.missingPermissionsReasons.officesContracts";
    }
    return null;
  };
  return { canStartTourId, missingPermissionsDetailKey, perms };
};

export {
  TOUR_NAV_DRAWER_EVENT,
  AVAILABLE_TOURS,
  tourDialogVisible,
  tutorialDialogDismissedThisSession,
};
export type { TourId, TourPermissions };
